import { BookOpen, Save, Trash2 } from "lucide-react";
import { lazy, Suspense, useEffect, useState, type JSX } from "react";

import type { WikiPageDTO } from "../../../common/api/types.js";
import { PrimaryButton } from "../../../common/components/PrimaryButton.js";
import { ShareDialog } from "../../../common/components/ShareDialog.js";
import { ShareStatusIcon, shareVisibilityLabel, visibilityFromShares } from "../../../common/components/ShareStatusIcon.js";
import type { ModulePageProps } from "../../ModuleDefinition.js";
import { useDeleteWikiPage, useRevokeWikiShare, useShareWikiPage, useUpdateWikiPage, useWikiPage, useWikiShares } from "../query/wikiQueries.js";
import { useWikiLocation } from "../wikiLocation.js";

const WikiEditor = lazy(() => import("../components/WikiEditor.js"));

export function WikiPage({ me }: ModulePageProps): JSX.Element {
  const location = useWikiLocation();
  const page = useWikiPage(location.pageId);
  const canEdit = me.role !== "GUEST";

  if (!location.pageId) return <WikiWelcome canEdit={canEdit} />;
  if (page.isPending) return <p className="m-auto text-sm text-ink-muted">Seite wird geladen …</p>;
  if (page.error || !page.data) return <p role="alert" className="m-auto text-sm text-danger">{page.error?.message ?? "Seite nicht gefunden."}</p>;
  return <WikiDocument key={page.data.id} page={page.data} canEdit={page.data.level === "EDIT" || page.data.level === "MANAGE"} onDeleted={() => location.openPage(null)} />;
}

function WikiDocument({ page, canEdit, onDeleted }: { page: WikiPageDTO; canEdit: boolean; onDeleted: () => void }): JSX.Element {
  const [title, setTitle] = useState(page.title);
  const [content, setContent] = useState(page.content);
  const [version, setVersion] = useState(page.version);
  const [dirty, setDirty] = useState(false);
  const [sharing, setSharing] = useState(false);
  const update = useUpdateWikiPage(page.id);
  const remove = useDeleteWikiPage();
  const shares = useWikiShares(page.id, page.level === "MANAGE");
  const share = useShareWikiPage(page.id);
  const revokeShare = useRevokeWikiShare(page.id);

  const save = (): void => {
    if (!canEdit || !title.trim()) return;
    update.mutate({ title, content, expectedVersion: version }, {
      onSuccess: (saved) => { setTitle(saved.title); setContent(saved.content); setVersion(saved.version); setDirty(false); },
    });
  };

  useEffect(() => {
    const keydown = (event: KeyboardEvent): void => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (dirty) save();
      }
    };
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  });

  const deletePage = (): void => {
    if (window.confirm(`„${page.title}“ samt Unterseiten wirklich löschen?`)) {
      remove.mutate(page.id, { onSuccess: onDeleted });
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 items-start gap-3 border-b border-line px-4 py-3 sm:px-6">
        <div className="min-w-0 flex-1">
          {canEdit ? <input value={title} onChange={(event) => { setTitle(event.target.value); setDirty(true); }} maxLength={160} aria-label="Seitentitel" className="w-full bg-transparent text-xl font-bold text-ink outline-none placeholder:text-ink-muted sm:text-2xl" /> : <h2 className="text-xl font-bold text-ink sm:text-2xl">{title}</h2>}
          <p className="mt-1 text-xs text-ink-muted">Version {version} · geändert von {page.updatedByDisplayName} am {new Date(page.updatedAt).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {page.level === "MANAGE" ? <button type="button" onClick={() => setSharing(true)} aria-label={shareVisibilityLabel(visibilityFromShares(shares.data ?? []))} title={shareVisibilityLabel(visibilityFromShares(shares.data ?? []))} className="grid h-9 w-9 place-items-center rounded-lg hover:bg-surface-sunken"><ShareStatusIcon visibility={visibilityFromShares(shares.data ?? [])} /></button> : null}
        {canEdit ? <>
          <button type="button" onClick={deletePage} disabled={remove.isPending} aria-label="Seite löschen" className="grid h-9 w-9 place-items-center rounded-lg text-ink-muted hover:bg-danger/10 hover:text-danger"><Trash2 className="h-4 w-4" /></button>
          <PrimaryButton type="button" onClick={save} disabled={!dirty || update.isPending || !title.trim()} className="gap-1.5"><Save className="h-4 w-4" /><span className="hidden sm:inline">{update.isPending ? "Speichert …" : "Speichern"}</span></PrimaryButton>
        </> : null}</div>
      </header>
      {(update.error ?? remove.error) ? <p role="alert" className="border-b border-line bg-danger/5 px-5 py-2 text-sm text-danger">{(update.error ?? remove.error)?.message}</p> : null}
      <Suspense fallback={<p className="m-auto text-sm text-ink-muted">Editor wird geladen …</p>}>
        <WikiEditor content={content} editable={canEdit} onChange={(markdown) => { setContent(markdown); setDirty(true); }} />
      </Suspense>
      {sharing ? <ShareDialog title={page.title} shares={shares.data ?? []} canManage={page.level === "MANAGE"} busy={share.isPending || revokeShare.isPending} error={share.error?.message ?? shares.error?.message ?? null} onShare={(input) => share.mutate(input)} onRevoke={(shareId) => revokeShare.mutate(shareId)} onClose={() => setSharing(false)} /> : null}
    </div>
  );
}

function WikiWelcome({ canEdit }: { canEdit: boolean }): JSX.Element {
  return <div className="m-auto max-w-md px-6 py-12 text-center"><span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-brand/10 text-brand"><BookOpen className="h-7 w-7" /></span><h2 className="mt-4 text-xl font-bold text-ink">Wissen gemeinsam festhalten</h2><p className="mt-2 text-sm leading-6 text-ink-muted">Wähle links eine Seite aus. Überschriften, Listen, Aufgaben, Links und Code lassen sich direkt schreiben oder über die Werkzeugleiste einfügen.</p>{canEdit ? <p className="mt-3 text-xs font-semibold text-brand">Neue Seiten und Unterseiten legst du in der Seitenleiste an.</p> : null}</div>;
}
